import UniDataTable from '@/common/classes/UniDataTable'

export const PROGRESS_STEPPER_COLUMNS = [
  'stepId',
  'stepName',
  'stepDescription',
  'stepStatus',
] as const

export interface ProgressStepperRow {
  stepId: number | string
  stepName: string
  stepDescription: string
  stepStatus: boolean
}

type TableLike = { columns?: unknown; rows?: unknown[]; attributes?: unknown }

const unwrap = (value: unknown): unknown => {
  if (value && typeof value === 'object' && 'value' in (value as object)) {
    return (value as { value?: unknown }).value
  }
  return value
}

const fieldValue = (row: any, name: string): unknown => {
  const fields =
    row && typeof row === 'object' && row.fields && typeof row.fields === 'object'
      ? row.fields
      : row
  if (fields && typeof fields === 'object') {
    return Object.prototype.hasOwnProperty.call(fields, name) ? fields[name] : undefined
  }
  if (row && typeof row.getField === 'function') return row.getField(name)
  return undefined
}

const asText = (value: unknown): string => {
  const v = unwrap(value)
  return v == null ? '' : String(v)
}

const asId = (value: unknown, fallback: number): number | string => {
  const v = unwrap(value)
  if (v == null || String(v).trim() === '') return fallback
  const n = Number(v)
  return Number.isFinite(n) ? n : String(v)
}

const asStatus = (value: unknown): boolean => {
  const v = unwrap(value)
  if (typeof v === 'boolean') return v
  if (typeof v === 'number') return v === 1
  const s = String(v ?? '')
    .trim()
    .toLowerCase()
  return s === '1' || s === 'true' || s === 'done' || s === 'completed'
}

export function isProgressStepperTable(value: unknown): boolean {
  const unwrapped = unwrap(value)
  return (
    unwrapped instanceof UniDataTable ||
    Boolean(
      unwrapped && typeof unwrapped === 'object' && Array.isArray((unwrapped as TableLike).rows),
    )
  )
}

export function normalizeProgressStepperRows(value: unknown): ProgressStepperRow[] {
  const unwrapped = unwrap(value)
  const rows: unknown[] =
    unwrapped instanceof UniDataTable
      ? unwrapped.rows
      : Array.isArray(unwrapped)
        ? unwrapped
        : ((unwrapped as TableLike | null)?.rows ?? [])

  const normalized = rows.map((row, index) => ({
    stepId: asId(fieldValue(row, 'stepId'), index),
    stepName: asText(fieldValue(row, 'stepName')) || `step-${index + 1}`,
    stepDescription:
      asText(fieldValue(row, 'stepDescription')) ||
      asText(fieldValue(row, 'stepName')) ||
      `Шаг ${index + 1}`,
    stepStatus: asStatus(fieldValue(row, 'stepStatus')),
  }))

  return normalized
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const av = a.row.stepId
      const bv = b.row.stepId
      if (typeof av === 'number' && typeof bv === 'number') return av - bv || a.index - b.index
      return String(av).localeCompare(String(bv), undefined, { numeric: true }) || a.index - b.index
    })
    .map(({ row }) => row)
}

export function createProgressStepperTable(rows: Array<Partial<ProgressStepperRow>>): UniDataTable {
  const table = new UniDataTable({
    columns: [...PROGRESS_STEPPER_COLUMNS],
    rows: [],
    attributes: {},
  })
  rows.forEach((input, index) => {
    table
      .newRow()
      .setFieldWithType('stepId', input.stepId ?? index, 'Long')
      .setFieldWithType('stepName', input.stepName ?? `step-${index + 1}`, 'String')
      .setFieldWithType(
        'stepDescription',
        input.stepDescription ?? input.stepName ?? `Шаг ${index + 1}`,
        'String',
      )
      .setFieldWithType('stepStatus', input.stepStatus ? 1 : 0, 'Long')
  })
  return table
}

export function toProgressStepperTypedValue(value: unknown): {
  type: 'UniDataTable'
  value: unknown
} {
  if (
    value &&
    typeof value === 'object' &&
    'type' in (value as object) &&
    'value' in (value as object)
  ) {
    return value as { type: 'UniDataTable'; value: unknown }
  }
  return {
    type: 'UniDataTable',
    value: value instanceof UniDataTable ? value : new UniDataTable(value as any),
  }
}
